import { organizationId } from '@/composables/seo/useSiteJsonLd';
import {
    ConfigurableProductObject,
    LipscoreProductObject,
    LipscoreProductReviewObject,
    PaazlDeliveryTimeObject,
    SeoBreadcrumbObject,
    StoreObject,
    TrustedShopsProductReviewObject,
    TrustedShopsProductReviewsObject,
} from '@/types/generated';
import type { ImageWithVariants } from '@/types/ImageTypes';
import { absoluteImageUrl, ImageTypeEnum } from '@/utils/imageUrl';
import { stripHtml } from '@/utils/stripHtml';
import _ from 'lodash';

type BuildProductJsonLdInput = {
    configurableProduct: ConfigurableProductObject;
    images: ImageWithVariants[];
    lipscoreProduct: LipscoreProductObject | null;
    reviews: LipscoreProductReviewObject[];
    /** Rates the product with Trusted Shops instead of Lipscore: one AggregateRating per product. */
    ratesWithTrustedShops?: boolean;
    trustedShopsReviews?: TrustedShopsProductReviewsObject | null;
    breadcrumbs: SeoBreadcrumbObject[];
    store: Pick<StoreObject, 'currency' | 'store_group' | 'default_country' | 'clean_root_url'>;
    /** Durations only — the dated payload is deferred, these are cacheable. */
    deliveryTimes?: PaazlDeliveryTimeObject[] | null;
};

type JsonLd = Record<string, unknown>;

const GTIN_PATTERN = /^(\d{8}|\d{12,14})$/;
const MAX_DESCRIPTION_LENGTH = 5000;
const MAX_IMAGES = 10;
const MAX_REVIEWS = 5;
// Extra-option USP slides: their caption ends with the `-uniquesellingpoint_…` name suffix that
// grutte-apier's BeautyShotNameSuffixService builds from the beauty shot configuration type.
const USP_IMAGE_PATTERN = /uniquesellingpoint/i;

export function buildProductJsonLd(input: BuildProductJsonLdInput): JsonLd {
    const product = input.configurableProduct;
    const identifier = product.group_identifier || undefined;
    const brand = input.store.store_group?.name;

    return withoutEmpty({
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `${product.canonical_url}#product`,
        name: product.name,
        url: product.canonical_url,
        sku: identifier,
        mpn: identifier,
        gtin: resolveGtin(product),
        description: resolveDescription(product),
        image: resolveImages(input.images, input.store.clean_root_url),
        brand: brand ? { '@type': 'Brand', name: brand } : undefined,
        category: resolveCategory(input.breadcrumbs) ?? undefined,
        offers: buildOffer(input),
        ...(input.ratesWithTrustedShops ? buildTrustedShopsRating(input.trustedShopsReviews) : buildLipscoreRating(input)),
    });
}

export function resolveCategory(breadcrumbs: SeoBreadcrumbObject[]): string | null {
    const trail = breadcrumbs.filter((breadcrumb: SeoBreadcrumbObject): boolean => !breadcrumb.is_current_page);

    if (trail.length <= 1) {
        return null;
    }

    return trail[trail.length - 1]?.title ?? null;
}

function withoutEmpty(data: JsonLd): JsonLd {
    return _.omitBy(data, (value: unknown): boolean => value === undefined || (Array.isArray(value) && value.length === 0));
}

function resolveGtin(product: ConfigurableProductObject): string | undefined {
    const ean = product.default_configurable_product_product?.metadata?.ean;

    return ean && GTIN_PATTERN.test(ean) ? ean : undefined;
}

function resolveDescription(product: ConfigurableProductObject): string | undefined {
    const description = stripHtml(product.description ?? '');

    return description ? _.truncate(description, { length: MAX_DESCRIPTION_LENGTH, separator: ' ' }) : undefined;
}

function resolveImages(images: ImageWithVariants[], rootUrl: string): string[] {
    const urls = images
        .filter((image: ImageWithVariants): boolean => !USP_IMAGE_PATTERN.test(image.caption ?? ''))
        .map((image: ImageWithVariants) => absoluteImageUrl(image, ImageTypeEnum.Full, rootUrl, ImageTypeEnum.Full));

    return _.uniq(_.compact(urls)).slice(0, MAX_IMAGES);
}

function buildOffer(input: BuildProductJsonLdInput): JsonLd | undefined {
    const data = input.configurableProduct.default_configurable_product_product?.product?.data;
    const productPrice = data?.product_price;
    const currency = input.store.currency;

    if (typeof productPrice !== 'number' || productPrice <= 0) {
        return undefined;
    }

    return withoutEmpty({
        '@type': 'Offer',
        priceCurrency: currency,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        url: input.configurableProduct.canonical_url,
        price: productPrice,
        sku: input.configurableProduct.group_identifier || undefined,
        seller: { '@id': organizationId(input.store) },
        priceSpecification: buildStrikethroughPrice(productPrice, data?.product_price_recommended, currency),
        shippingDetails: buildShippingDetailsList(input.deliveryTimes, currency, input.store.default_country),
    });
}

function buildStrikethroughPrice(price: number, recommendedPrice: number | null | undefined, currency: string): JsonLd | undefined {
    if (!recommendedPrice || recommendedPrice <= price) {
        return undefined;
    }

    return {
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: recommendedPrice,
        priceCurrency: currency,
    };
}

// Google requires a destination, so a store without a country gets no shipping details at all.
function buildShippingDetailsList(deliveryTimes: PaazlDeliveryTimeObject[] | null | undefined, currency: string, country: string | null): JsonLd[] {
    if (!country) {
        return [];
    }

    return (deliveryTimes ?? []).map((option: PaazlDeliveryTimeObject) => buildShippingDetails(option, currency, country));
}

function buildShippingDetails(option: PaazlDeliveryTimeObject, currency: string, country: string): JsonLd {
    return withoutEmpty({
        '@type': 'OfferShippingDetails',
        shippingLabel: option.name,
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: country },
        shippingRate: { '@type': 'MonetaryAmount', value: option.rate, currency },
        deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            transitTime: {
                '@type': 'QuantitativeValue',
                minValue: _.clamp(option.delivery_time_in_days, 0, 1),
                maxValue: option.delivery_time_in_days,
                unitCode: 'DAY',
            },
        },
    });
}

function buildLipscoreRating(input: BuildProductJsonLdInput): JsonLd {
    return {
        aggregateRating: buildAggregateRating(input.lipscoreProduct),
        review: _.compact(input.reviews.map(buildReview)).slice(0, MAX_REVIEWS),
    };
}

function buildTrustedShopsRating(reviews: TrustedShopsProductReviewsObject | null | undefined): JsonLd {
    if (!reviews?.rating) {
        return {};
    }

    return {
        aggregateRating: { '@type': 'AggregateRating', ratingValue: reviews.rating, ratingCount: reviews.count, bestRating: 5, worstRating: 1 },
        review: _.compact(reviews.reviews.map(buildTrustedShopsReview)).slice(0, MAX_REVIEWS),
    };
}

function buildTrustedShopsReview(review: TrustedShopsProductReviewObject): JsonLd | undefined {
    if (!review.author || !review.comment) {
        return undefined;
    }

    return {
        '@type': 'Review',
        author: { '@type': 'Person', name: review.author },
        reviewRating: { '@type': 'Rating', ratingValue: review.rating, bestRating: 5, worstRating: 1 },
        reviewBody: review.comment,
        datePublished: review.submitted_at.slice(0, 10),
    };
}

function buildAggregateRating(lipscoreProduct: LipscoreProductObject | null): JsonLd | undefined {
    if (!lipscoreProduct || lipscoreProduct.votes <= 0) {
        return undefined;
    }

    return withoutEmpty({
        '@type': 'AggregateRating',
        ratingValue: lipscoreProduct.rating,
        ratingCount: lipscoreProduct.votes,
        reviewCount: lipscoreProduct.review_count > 0 ? lipscoreProduct.review_count : undefined,
        bestRating: 5,
        worstRating: 1,
    });
}

function buildReview(review: LipscoreProductReviewObject): JsonLd | undefined {
    const author = review.author ?? review.user?.short_name;

    if (!author || !review.text || review.rating === null) {
        return undefined;
    }

    return withoutEmpty({
        '@type': 'Review',
        author: { '@type': 'Person', name: author },
        reviewRating: { '@type': 'Rating', ratingValue: review.rating, bestRating: 5, worstRating: 1 },
        reviewBody: review.text,
        datePublished: review.created_at?.slice(0, 10),
    });
}
